import Link from "next/link";
import type { ReactNode } from "react";
import { ArrowRight } from "./icons";

type Variant = "primary" | "secondary" | "outline";

const VARIANTS: Record<Variant, string> = {
  primary:
    "bg-purple-500 text-white shadow-[0_8px_30px_rgba(147,51,234,0.35)] hover:bg-purple-400 hover:shadow-[0_8px_40px_rgba(168,85,247,0.55)]",
  secondary:
    "bg-surface-overlay text-white border border-white/10 hover:border-white/20 hover:bg-surface-overlay/80",
  outline:
    "bg-transparent text-white border-[1.5px] border-white/20 hover:border-white/40 hover:bg-white/5",
};

type Props = {
  children: ReactNode;
  href?: string;
  variant?: Variant;
  withArrow?: boolean;
  className?: string;
  full?: boolean;
  newTab?: boolean;
  onClick?: () => void;
};

export default function Button({
  children,
  href,
  variant = "primary",
  withArrow = variant === "primary",
  className,
  full,
  newTab,
  onClick,
}: Props) {
  const cls = `group inline-flex items-center justify-center gap-2 rounded-[10px] px-7 py-3.5 font-sans text-[15px] font-semibold tracking-tight transition-all duration-300 outline-none focus-visible:ring-2 focus-visible:ring-purple-400/70 ${
    VARIANTS[variant]
  } ${full ? "w-full" : ""} ${className ?? ""}`;

  const inner = (
    <>
      {children}
      {withArrow && (
        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
      )}
    </>
  );

  if (href) {
    return (
      <Link
        href={href}
        className={cls}
        {...(newTab ? { target: "_blank", rel: "noopener noreferrer" } : {})}
      >
        {inner}
      </Link>
    );
  }
  return (
    <button type="button" className={cls} onClick={onClick}>
      {inner}
    </button>
  );
}
